import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { SlidersHorizontal, X, Loader2, Search } from "lucide-react";
import { STORE_API } from "../store/CartContext";
import ProductCard from "../components/store/ProductCard";
import { CATEGORIES, MENU, SUBCATEGORIES } from "../data/shopMenu";

const CATS = Object.entries(CATEGORIES);
const PRICES = [["", "", "Any price"], ["0", "299", "Under ₹299"], ["300", "699", "₹300 – ₹699"], ["700", "1499", "₹700 – ₹1,499"], ["1500", "", "₹1,500 & above"]];
const SORTS = [["popular", "Popularity"], ["newest", "Newest"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"], ["discount", "Biggest discount"]];

function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${active ? "border-[#3915AA] bg-[#3915AA] text-white" : "border-[#3915AA]/15 bg-white text-[#1E0A5C] hover:border-[#3915AA]/40"}`}>{children}</button>
  );
}

function Filters({ params, set, brands }) {
  const g = (k) => params.get(k) || "";
  const brandSel = g("brand") ? g("brand").split(",") : [];
  const toggleBrand = (b) => set("brand", (brandSel.includes(b) ? brandSel.filter((x) => x !== b) : [...brandSel, b]).join(","));
  const Block = ({ title, children }) => <div className="border-b border-[#3915AA]/10 py-4"><p className="mb-3 text-xs font-bold uppercase tracking-wider text-[#3915AA]">{title}</p><div className="flex flex-wrap gap-2">{children}</div></div>;
  return (
    <div data-testid="shop-filters">
      <Block title="Pet">{["", "Dog", "Cat"].map((p) => <Chip key={p} active={g("pet") === p} onClick={() => set("pet", p)} testid={`filter-pet-${p || "all"}`}>{p || "All"}</Chip>)}</Block>
      <Block title="Price">{PRICES.map(([mn, mx, l]) => <Chip key={l} active={g("min_price") === mn && g("max_price") === mx} onClick={() => set({ min_price: mn, max_price: mx })} testid={`filter-price-${mn || "any"}`}>{l}</Chip>)}</Block>
      {brands.length > 0 && <Block title="Brand">{brands.map((b) => <Chip key={b} active={brandSel.includes(b)} onClick={() => toggleBrand(b)} testid={`filter-brand-${b}`}>{b}</Chip>)}</Block>}
      <div className="py-4">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-[#1E0A5C]">
          <input type="checkbox" checked={g("in_stock") === "true"} onChange={(e) => set("in_stock", e.target.checked ? "true" : "")} data-testid="filter-in-stock" className="h-4 w-4 accent-[#FC5106]" /> In stock only
        </label>
      </div>
    </div>
  );
}

export default function ShopListing() {
  const { category } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [q, setQ] = useState(params.get("q") || "");

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    Object.entries(typeof k === "object" ? k : { [k]: v }).forEach(([kk, vv]) => (vv ? next.set(kk, vv) : next.delete(kk)));
    next.delete("page");
    setParams(next);
  };

  useEffect(() => {
    setData(null);
    const p = Object.fromEntries(params.entries());
    axios.get(`${STORE_API}/products`, { params: { ...p, category, per_page: 24 } }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, brands: [] }));
  }, [params, category]);

  const pet = params.get("pet");
  const subs = category ? (pet && MENU[pet] ? MENU[pet].find(([c]) => c === category)?.[1] || [] : SUBCATEGORIES[category] || []) : [];
  const sub = params.get("sub");
  const base = category ? sub || CATEGORIES[category] : params.get("q") ? `Results for "${params.get("q")}"` : params.get("brand") ? params.get("brand") : "All products";
  const title = pet && !params.get("q") ? `${base} for ${pet === "Dog" ? "Dogs" : "Cats"}` : base;

  return (
    <div data-testid="shop-listing-page" className="min-h-screen bg-[#F7F5FF] text-[#1E0A5C]">
      <div className="bg-[#3915AA]">
        <div className="container-custom py-8">
          <p className="text-xs text-white/60"><Link to="/shop" className="hover:text-white">Shop</Link> / {title}</p>
          <h1 className="mt-2 font-heading text-3xl font-bold text-white sm:text-4xl">{title}</h1>
          <form onSubmit={(e) => { e.preventDefault(); set("q", q.trim()); }} className="relative mt-5 max-w-lg">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#3915AA]/50" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search food, toys, shampoo…" data-testid="shop-search-input" className="w-full rounded-full border-0 bg-white py-3 pl-11 pr-4 text-sm text-[#1E0A5C] outline-none focus:ring-2 focus:ring-[#FC5106]" />
          </form>
          <div className="mt-5 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
            <Link to={`/shop/all${pet ? `?pet=${pet}` : ""}`} className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-bold ${!category ? "bg-[#FC5106] text-white" : "bg-white/10 text-white hover:bg-white/20"}`}>All</Link>
            {CATS.map(([slug, name]) => (
              <Link key={slug} to={`/shop/c/${slug}${pet ? `?pet=${pet}` : ""}`} data-testid={`listing-cat-${slug}`} className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-bold ${category === slug ? "bg-[#FC5106] text-white" : "bg-white/10 text-white hover:bg-white/20"}`}>{name}</Link>
            ))}
          </div>
          {subs.length > 0 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]" data-testid="listing-subcategories">
              <button onClick={() => set("sub", "")} className={`whitespace-nowrap rounded-full border px-3.5 py-1 text-xs font-semibold ${!sub ? "border-white bg-white text-[#3915AA]" : "border-white/30 text-white hover:bg-white/10"}`}>All</button>
              {subs.map((s) => (
                <button key={s} onClick={() => set("sub", s)} data-testid={`listing-sub-${s.replace(/\W+/g, "-")}`} className={`whitespace-nowrap rounded-full border px-3.5 py-1 text-xs font-semibold ${sub === s ? "border-white bg-white text-[#3915AA]" : "border-white/30 text-white hover:bg-white/10"}`}>{s}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      <section className="container-custom grid gap-8 py-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block"><div className="sticky top-28 rounded-2xl bg-white px-5"><Filters params={params} set={set} brands={data?.brands || []} /></div></aside>
        <div>
          <div className="mb-5 flex items-center justify-between gap-3">
            <p className="text-sm text-[#5E5675]" data-testid="listing-count">{data ? `${data.total} products` : "Loading…"}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setMobileFilters(true)} data-testid="mobile-filters-btn" className="inline-flex items-center gap-1.5 rounded-full border border-[#3915AA]/20 bg-white px-3.5 py-2 text-xs font-bold lg:hidden"><SlidersHorizontal className="h-3.5 w-3.5" /> Filters</button>
              <select value={params.get("sort") || "popular"} onChange={(e) => set("sort", e.target.value)} data-testid="listing-sort" className="rounded-full border border-[#3915AA]/20 bg-white px-3.5 py-2 text-xs font-bold text-[#1E0A5C] outline-none">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          {!data ? (
            <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#FC5106]" /></div>
          ) : data.items.length === 0 ? (
            <div data-testid="listing-empty" className="rounded-2xl bg-white py-20 text-center">
              <p className="font-heading text-xl font-bold">No products match</p>
              <button onClick={() => setParams({})} className="mt-3 text-sm font-bold text-[#FC5106]">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 xl:grid-cols-4">{data.items.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          )}
        </div>
      </section>

      {mobileFilters && (
        <div className="fixed inset-0 z-[70] bg-black/50 lg:hidden" onClick={() => setMobileFilters(false)}>
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-3xl bg-white px-5 pb-6" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between bg-white py-4"><p className="font-heading text-lg font-bold">Filters</p><button onClick={() => setMobileFilters(false)} data-testid="mobile-filters-close"><X className="h-5 w-5" /></button></div>
            <Filters params={params} set={set} brands={data?.brands || []} />
            <button onClick={() => setMobileFilters(false)} className="w-full rounded-full bg-[#FC5106] py-3 text-sm font-bold text-white">Show {data?.total ?? ""} products</button>
          </div>
        </div>
      )}
    </div>
  );
}
